import React, { useState, useEffect } from "react"
import { Form, Input, FormGroup, Container, Alert, Label } from "reactstrap"
import { Trans, useLingui } from "@lingui/react"
import PropTypes from "prop-types"
import { useSelector, useDispatch } from "react-redux"
import {
  createMember,
} from "../../store/actions/members/createMember"
import uuid from "uuid"
import validateInput from "../../view/shared/validations/addMember"
import "react-dates/initialize"
import "react-dates/lib/css/_datepicker.css"
import moment from "moment"
import PhoneInput from "react-phone-input-2"
import "react-phone-input-2/lib/style.css"
import ar from "react-phone-input-2/lang/ar.json"
import MultipleSelect from "../layout/MulitpleSelect"
import { defaultDate } from "../../view/shared/utilities"
import StellarLoader from "../layout/StellarLoader"
import { triggerAction } from "../../store/constants"
import { deleteCacheByKey, resetRequestsErrors } from "../../view/shared/utilities/cache"

const headerStyle = {
  color: "#E36F1E",
  fontFamily: "quicksand",
  fontWeight: "bolder",
  fontSize: "1.8em",
  textAlign: "center",
  paddingBottom: "0.5em",
}

const formStyle = {
  flex: "10",
  padding: "5px",
  fontFamily: "Fira Sans",
  fontSize: "17px",
  height: "50px",
}

const errorStyle = {
  fontFamily: "Fira Sans",
  fontSize: "17px",
  color: "red",
}

const ErrorMessage = ({ msg }) => {
  if (msg) {
    return <div style={failStyle}>{msg}</div>
  }
  return (
    <div style={failStyle}>
      <Trans id="Error creating member">Error creating member</Trans>
    </div>
  )
}

ErrorMessage.propTypes = {
  msg: PropTypes.string,
}

const AddMember = () => {
  const { i18n } = useLingui()
  const dispatch = useDispatch()
  const { group, members, isCreating, memberToEdit } = useSelector(
    (state) => ({
      group: state.group,
      members: state.loadedvslaMembers,
      isCreating: state.isCreatingVslaMember,
      memberToEdit: state.memberToEdit,
    }),
  )

  const initialState = {
    name: "",
    phone_number: "",
    member_uuid: uuid.v4(),
    date_of_birth: defaultDate(),
    role_in_group: "",
    paysInterest: "",
    gender: "",
    errors: {},
    isloading: false,
    nationalities: [],
    lang: i18n.locale,
    _country: localStorage.getItem("_country") || "GH",
  }

  const [state, setState] = useState(initialState)

  useEffect(() => {
    if (memberToEdit) {
      setState((prevState) => ({
        ...prevState,
        name: memberToEdit.full_name || "",
        phone_number: memberToEdit.phone
          ? memberToEdit.phone.replace("+", "")
          : "",
        member_uuid: memberToEdit.pubkey || uuid.v4(),
        date_of_birth: memberToEdit.date_of_birth
          ? moment(memberToEdit.date_of_birth).format("YYYY-MM-DD")
          : defaultDate(),
        role_in_group: memberToEdit.member_role || "",
        paysInterest:
          memberToEdit.loan_service_charge === true
            ? "Yes"
            : memberToEdit.loan_service_charge === false
              ? "No"
              : "",
        gender: memberToEdit.gender || "",
        nationalities: memberToEdit.nationalities || [],
      }))
    } else {
      setState(initialState)
    }
  }, [memberToEdit, dispatch])


  const onChange = (e) => {
    const updatedErrors = { ...state.errors }
    delete updatedErrors[e.target.name]
    setState((prevState) => ({
      ...prevState,
      errors: updatedErrors,
      [e.target.name]: e.target.value,
    }))
  }

  const isValid = () => {
    const data = { ...state }
    delete data.paysInterest
    const { errors, isValid } = validateInput(
      ["ar", "lb"].includes(i18n.locale) ? data : state,
    )

    if (!isValid) {
      setState((prevState) => ({ ...prevState, errors }))
      return isValid
    }
    setState((prevState) => ({ ...prevState, errors: {} }))
    return isValid
  }

  const onSubmit = async (e) => {
    e.preventDefault()
    if (isValid()) {
      if (memberToEdit?.cacheId) {
        deleteCacheByKey("offlineRequests", "tempId", memberToEdit.cacheId)
        dispatch(triggerAction("CLEAR_MEMBER_TO_EDIT"))
        await resetRequestsErrors()
      }
      addMemberBackEndTwo(
        state.name,
        state.phone_number,
        state.member_uuid,
        state.date_of_birth,
        state.role_in_group,
        state.paysInterest,
        state.gender,
        state.nationalities,
      ).then((error) => {
        if (error) {
          return
        }
        setState(initialState)
      })
    }
  }

  const phoneValidate = (phone) => {
    if (phone === "+") {
      return null
    } else {
      return phone
    }
  }

  const addMemberBackEndTwo = async (
    name,
    phone_number,
    uuidValue,
    date_of_birth,
    role_in_group,
    paysInterest,
    gender,
    nationalities,
  ) => {
    const lang = i18n.locale
    const group_name = group.name

    const notif = {
      en: {
        message: `You have been added as a member of ${group_name} savings group`,
      },
      fr: {
        message: `You have been added as a member of ${group_name} savings group`,
      },
      sw: {
        message: `Umeandikishwa kuwa mwanachama wa kikundi cha akiba ${group_name}`,
      },
      es: {
        message: `Se le ha agregado como miembro del grupo de ahorro ${group_name}`,
      },
      lb: {
        message: `You have been added as a household of ${group_name} community`,
      },
      pt: {
        message: `Você foi adicionado como membro do grupo de poupança ${group_name}`,
      },
      ar: {
        message: group_name,
      },
    }

    const finalDate = date_of_birth === null ? new Date() : new Date(date_of_birth)
    const dob = moment(finalDate).format("YYYY-MM-DD")

    const member = {
      gender,
      nationalities,
      full_name: name,
      phone: phoneValidate("+" + phone_number),
      pubkey: uuidValue,
      date_of_birth: dob,
      member_role: role_in_group,
      status: "Active",
      loan_service_charge: paysInterest === "Yes" ? true : false,
      msg: Object.values(notif[lang])[0],
    }

    return dispatch(createMember(member))
  }

  const getPhoneNumbers = () => {
    let phoneArray = []
    members.forEach((member) => {
      if (member.blockchain_wallet_id) {
        phoneArray.push(phoneValidate(member.phone))
      }
    })
    return phoneArray.filter((n) => n)
  }

  const { errors, lang, _country } = state,
    en = false
  const phoneArray = getPhoneNumbers()

  if (isCreating || state.isloading) {
    return <StellarLoader />
  }

  return (
    <div>
      <div
        style={{
          backgroundColor: "#white",
          padding: ".5rem",
          textAlign: "center",
          margin: "34px 0 0 0",
        }}
      >
        <h4 style={headerStyle}>
          {memberToEdit ? (
            <Trans id="Edit Member" />
          ) : (
            <Trans id="PERSONAL INFORMATION" />
          )}
        </h4>
        {memberToEdit?.error && <small style={{
          color: 'red',
          margin: 'auto',
          fontSize: '14px'
        }}>{memberToEdit?.error}</small>}
      </div>


      <Form onSubmit={onSubmit} noValidate>
        <Container>
          <FormGroup>
            <Label style={loadingStyle}>
              <Trans id="Full name">Full name</Trans>
            </Label>
            <Input
              type="text"
              name="name"
              style={formStyle}
              placeholder={i18n._("Full name")}
              onChange={onChange}
              value={state.name}
            />
            <span style={errorStyle}>{errors.name}</span>
          </FormGroup>

          <FormGroup
            style={{
              direction: "ltr",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Label
              style={{
                ...loadingStyle,
                alignSelf:
                  i18n.locale === "ar" ? "end" : "initial",
              }}
            >
              <Trans id="Phone number">Phone number</Trans>
            </Label>
            <PhoneInput
              country={_country.toLocaleLowerCase()}
              placeholder={i18n._("Phone number")}
              error={errors.phone_number}
              enableSearch
              disableSearchIcon
              style={loadingStyle}
              inputStyle={{
                flex: "10",
                fontFamily: "Fira Sans",
                height: "50px",
                width: "100%",
                fontSize: "20px",
              }}
              value={state.phone_number}
              onChange={(phone_number) =>
                setState((prevState) => ({ ...prevState, phone_number }))
              }
              limitMaxLength={true}
              localization={lang === "ar" ? ar : en}
            />
            <span style={errorStyle}>{errors.phone_number}</span>
            {phoneArray.includes("+" + state.phone_number) &&
              (!memberToEdit ||
                memberToEdit.phone !== "+" + state.phone_number) ? (
              <span style={errorStyle}>
                <Trans id="Number in Use">Number in Use</Trans>
              </span>
            ) : (
              <span></span>
            )}
          </FormGroup>

          <FormGroup
            style={{
              direction: "ltr",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Label
              style={{
                ...loadingStyle,
                alignSelf:
                  i18n.locale === "ar" ? "end" : "initial",
              }}
            >
              <Trans id="Date of birth">Date of birth</Trans>
            </Label>
            <Input
              type="date"
              name="date_of_birth"
              placeholder={i18n._("Date of birth")}
              style={formStyle}
              onChange={onChange}
              value={state.date_of_birth}
              max={defaultDate()}
            />
            <span style={errorStyle}>{errors.date_of_birth}</span>
          </FormGroup>
          {["ar", "lb"].includes(i18n.locale) && (
            <MultipleSelect
              title={i18n._("Nationality")}
              preSetItems={state.nationalities}
              callback={(items) => {
                setState((prevState) => ({ ...prevState, nationalities: items }))
              }}
              placeholderOption={i18n._("Select Nationalities")}
              options={[
                {
                  value: i18n._("Lebanon"),
                  title: i18n._("Lebanon"),
                },
                {
                  value: i18n._("Syria"),
                  title: i18n._("Syria"),
                },
                {
                  value: i18n._("Palestine"),
                  title: i18n._("Palestine"),
                },
              ]}
            />
          )}
          <FormGroup>
            <Label
              style={{
                ...loadingStyle,
                textAlign:
                  i18n.locale === "ar" ? "right" : "initial",
                width:
                  i18n.locale === "ar" ? "100%" : "initial",
              }}
            >
              <Trans id="gender">Gender</Trans>
            </Label>
            <Input
              id="gender"
              type="select"
              name="gender"
              style={formStyle}
              onChange={onChange}
              value={state.gender}
            >
              <option disabled value="">
                <Trans id="gender">Gender</Trans>
              </option>
              <option value="female">
                <Trans id="female">Female</Trans>
              </option>
              <option value="male">
                <Trans id="male">Male</Trans>
              </option>
            </Input>
            <span style={errorStyle}>{errors.gender}</span>
          </FormGroup>
          <FormGroup>
            <Label
              style={{
                ...loadingStyle,
                textAlign:
                  i18n.locale === "ar" ? "right" : "initial",
                width:
                  i18n.locale === "ar" ? "100%" : "initial",
              }}
            >
              <Trans id="Role in group">Role in group</Trans>
            </Label>
            <Input
              id="select_role"
              type="select"
              name="role_in_group"
              style={formStyle}
              onChange={onChange}
              value={state.role_in_group}
            >
              <option value="">
                <Trans id="Role in group">Role in group</Trans>
              </option>
              <option>
                <Trans id="Chairperson">Chairperson</Trans>
              </option>
              {!["ar", "lb"].includes(i18n.locale) && (
                <>
                  <option>
                    <Trans id="Secretary">Secretary</Trans>
                  </option>
                  <option>
                    <Trans id="Box keeper">Box keeper</Trans>
                  </option>
                  <option>
                    <Trans id="Money counter">Money counter</Trans>
                  </option>
                  <option>
                    <Trans id="Organiser">Organiser</Trans>
                  </option>
                  <option>
                    <Trans id="Key Holder">Key Holder</Trans>
                  </option>
                </>
              )}
              <option>
                <Trans id="Member">Member</Trans>
              </option>
            </Input>
            <span style={errorStyle}>{errors.role_in_group}</span>
          </FormGroup>
          {!["ar", "lb"].includes(i18n.locale) && (
            <FormGroup>
              <Label style={loadingStyle}>
                <Trans id="Does member pay loan interest for loans?">Does member pay loan interest for loans?</Trans>
              </Label>
              <Input
                id="paysInterest"
                type="select"
                name="paysInterest"
                style={formStyle}
                onChange={onChange}
                value={state.paysInterest}
              >
                <option value="">
                  <Trans id="Does member pay loan interest for loans?">Does member pay loan interest for loans?</Trans>
                </option>
                <option value="Yes">
                  <Trans id="Yes">Yes</Trans>
                </option>
                <option value="No">
                  <Trans id="No">No</Trans>
                </option>
              </Input>
              <span style={errorStyle}>{errors.paysInterest}</span>
            </FormGroup>
          )}
          <Alert color="warning">
            <span>
              <Trans id="By proceeding to create the account, you confirm that you are 18 years of age and above">
                By proceeding to create the account, you confirm that you are 18
                years of age and above
              </Trans>
            </span>
          </Alert>
        </Container>
        <Container>
          <FormGroup>
            <Input
              type="submit"
              value={
                memberToEdit
                  ? i18n._("Update Member")
                  : i18n._("Create Member")
              }
              name="submit"
              className="btn btn-primary"
              disabled={
                phoneArray.includes("+" + state.phone_number) &&
                (!memberToEdit ||
                  memberToEdit.phone !== "+" + state.phone_number)
              }
            />
          </FormGroup>
        </Container>
      </Form>
    </div>
  )
}

const loadingStyle = {
  fontFamily: "Fira Sans",
  fontSize: "20px",
}

const failStyle = {
  fontFamily: "Fira Sans",
  fontSize: "20px",
  color: "red",
  fontWeight: "bold",
}

export default AddMember
